import React, { createContext, ReactNode, useContext, useState } from 'react';

export interface AccordionControlContext {
  isExpanded: (id: string) => boolean;
  setExpanded: (id: string, state: boolean) => void;
  toggleExpanded: (id: string, element: HTMLDivElement) => void;
}

const AccordionControlContextContext = createContext<AccordionControlContext>({
  isExpanded: () => false,
  setExpanded: () => {},
  toggleExpanded: () => {},
});

export function useAccordionControl() {
  return useContext(AccordionControlContextContext);
}

export function AccordionControlProvider({
  children,
  single = false,
  scrollIntoView = false,
  initialState = {},
}: {
  children: ReactNode;
  single?: boolean;
  scrollIntoView?: boolean;
  initialState?: { [id: string]: boolean };
}) {
  const [expandedList, setExpandedList] = useState<{ [id: string]: boolean | undefined }>(initialState);

  const isExpanded = (id: string) => !!expandedList[id];

  const setExpanded = (id: string, value: boolean) => {
    if (single) {
      setExpandedList({ [id]: value });
    } else {
      setExpandedList(c => ({ ...c, [id]: value }));
    }
  };

  const toggleExpanded = (id: string, element?: HTMLDivElement) => {
    if (scrollIntoView && element && !isExpanded(id)) {
      setTimeout(() => element.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 500); // duration defined by Sherpa
    }
    setExpanded(id, !isExpanded(id));
  };

  return (
    <AccordionControlContextContext.Provider value={{ isExpanded, setExpanded, toggleExpanded }}>
      {children}
    </AccordionControlContextContext.Provider>
  );
}
